import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import { Button } from '../../../../shared/components/Button.js';
import { brandingBasisForUom } from '../../../../shared/contracts/branding-line.js';
import { orderLineQuantityLabel } from './orderLineQuantity.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import {
  listDealers,
  loadBrandingCatalogue,
  loadRequesters,
  createBrandingRequest,
  getBrandingRequest,
  forwardRequestLines,
  listBrandingRequests,
  type Dealer,
  type BrandingCatalogue,
  type Requesters,
  type BrandingRequestDetail,
  type RequestSummary,
} from '../../api/brandingRequestApi.js';
import {
  listWorkOrderCover,
  type WorkOrderCoverLine,
} from '../../api/brandingWorkOrderApi.js';
import { DateInput } from '../../../../shared/components/DateInput.js';
import { currentCycleId } from '../../api/vendorBrandingProfileApi.js';

interface DraftLine {
  key: string;
  brandingTypeId: string;
  brandingItemId: string;
  quantity: string;
  height: string;
  width: string;
  slotLength: string;
  slotUnit: string;
  siteLocation: string;
  fromDate: string;
  toDate: string;
  remarks: string;
  /** The approved work order line this row draws on. */
  workOrderLineId: string;
}

const newLine = (): DraftLine => ({
  key: Math.random().toString(36).slice(2),
  brandingTypeId: '',
  brandingItemId: '',
  quantity: '',
  height: '',
  width: '',
  slotLength: '',
  slotUnit: 'SECOND',
  siteLocation: '',
  fromDate: '',
  toDate: '',
  remarks: '',
  workOrderLineId: '',
});

/**
 * Raise a branding request.
 *
 * The screen asks for a branding type and a sub-type, and nothing else until
 * those are chosen. The sub-type carries the unit it is measured in, and that
 * unit decides the rest of the row — a height and a width for work measured by
 * area, a slot length and a date window for broadcast time, a plain count for
 * everything else. This is the same rule the work order uses, read from the
 * same shared contract, so the two documents cannot drift apart.
 *
 * A dealer is asked for only where the sub-type says the work happens on the
 * dealer's own property. Everywhere else the server files the request against
 * the tenant's placeholder dealer, so reports still have a row to group by.
 *
 * The request number is issued by the server on save — it is a running number
 * per financial year and district, so it cannot be predicted here.
 */
export function BrandingRequestNewPage(): ReactNode {
  const { token } = useAuth();
  const { dayRange, at } = useBusinessDay();
  // Stored quantities and rates are written exactly, grouped the tenant's way.
  const { decimal, number } = useBusinessNumber();
  const [dealers, setDealers] = useState<readonly Dealer[]>([]);
  const [cat, setCat] = useState<BrandingCatalogue | null>(null);
  const [people, setPeople] = useState<Requesters | null>(null);
  const [deptId, setDeptId] = useState('');
  const [memberId, setMemberId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [cycleId, setCycleId] = useState('');
  const [remarks, setRemarks] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([newLine()]);
  const [saved, setSaved] = useState<BrandingRequestDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  // Saved orders, so one that still has draft rows can be reopened and sent on.
  const [orders, setOrders] = useState<readonly RequestSummary[]>([]);
  // What each sub-type is covered by, keyed by "typeId|itemId" so two rows
  // asking for the same thing are answered once.
  const [cover, setCover] = useState<
    Readonly<Record<string, readonly WorkOrderCoverLine[] | 'loading' | 'failed'>>
  >({});

  const coverKey = (l: DraftLine) => `${l.brandingTypeId}|${l.brandingItemId}`;
  const coverOf = (l: DraftLine) =>
    l.brandingItemId ? cover[coverKey(l)] : undefined;
  const allCoverLines = (l: DraftLine): readonly WorkOrderCoverLine[] => {
    const c = coverOf(l);
    return Array.isArray(c) ? c : [];
  };
  /**
   * The vendor this order already goes to. The order is one job and one job
   * goes to one vendor, so once any other row draws on a vendor's work order,
   * this row is offered only that vendor's. The server refuses a mix anyway.
   */
  const jobVendorFor = (l: DraftLine): WorkOrderCoverLine | null => {
    for (const o of lines) {
      if (o.key === l.key || !o.workOrderLineId) continue;
      const c = allCoverLines(o).find((x) => x.workOrderLineId === o.workOrderLineId);
      if (c) return c;
    }
    return null;
  };
  const coverLines = (l: DraftLine): readonly WorkOrderCoverLine[] => {
    const v = jobVendorFor(l);
    const all = allCoverLines(l);
    return v ? all.filter((c) => c.vendorId === v.vendorId) : all;
  };
  const chosenWorkOrder = (l: DraftLine) =>
    coverLines(l).find((c) => c.workOrderLineId === l.workOrderLineId) ?? null;

  /**
   * Only the team that issues work orders names one here. A sales officer
   * raises the requirement and the branding team attaches the work order when
   * the order reaches them.
   */
  const namesAWorkOrder = useMemo(
    () =>
      (people?.members ?? []).find((m) => m.userId === memberId)
        ?.issuesWorkOrders === true,
    [people, memberId],
  );

  // Which sub-types have already been asked about, so the effect can drop
  // `cover` from its dependencies. Keeping it there made the effect re-run on
  // its own 'loading' write, whose cleanup then discarded the answer still in
  // flight — the row sat on "Looking for work orders…" for ever.
  const asked = useRef<Set<string>>(new Set());
  const savedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!namesAWorkOrder) return;
    for (const l of lines) {
      if (!l.brandingTypeId || !l.brandingItemId) continue;
      const key = coverKey(l);
      if (asked.current.has(key)) continue;
      asked.current.add(key);
      setCover((c) => ({ ...c, [key]: 'loading' }));
      listWorkOrderCover(l.brandingTypeId, l.brandingItemId, token)
        .then((r) => setCover((c) => ({ ...c, [key]: r.data })))
        // An unread cover is not "no work order issued" — that would send the
        // requester off to issue one that may already exist.
        .catch((e) => {
          setCover((c) => ({ ...c, [key]: 'failed' }));
          setError(`Work orders could not be loaded: ${(e as Error).message}`);
        });
    }
  }, [lines, token, namesAWorkOrder]);

  useEffect(() => {
    let alive = true;
    Promise.all([
      listDealers(token).catch((e) => {
        if (alive) setError(`Dealers could not be loaded: ${(e as Error).message}`);
        return [] as readonly Dealer[];
      }),
      loadBrandingCatalogue(token).catch((e) => {
        if (alive) setError(`Branding types could not be loaded: ${(e as Error).message}`);
        return null;
      }),
      loadRequesters(token).catch((e) => {
        if (alive) setError(`Departments could not be loaded: ${(e as Error).message}`);
        return null;
      }),
    ]).then(([d, c, p]) => {
      if (!alive) return;
      setDealers(d);
      setCat(c);
      setPeople(p);
      if (c) setCycleId((prev) => prev || currentCycleId(c.cycles));
    });
    return () => { alive = false; };
  }, [token]);

  const membersOf = (dept: string) =>
    (people?.members ?? []).filter((m) => m.deptId === dept);

  const dealer = useMemo(
    () => dealers.find((d) => d.customerId === customerId) ?? null,
    [dealers, customerId],
  );

  const itemsOf = (typeId: string) =>
    (cat?.items ?? []).filter((i) => i.brandingTypeId === typeId);

  const subTypeOf = (l: DraftLine) =>
    (cat?.items ?? []).find((i) => i.brandingItemId === l.brandingItemId) ?? null;

  const basisOf = (l: DraftLine) =>
    brandingBasisForUom(subTypeOf(l)?.uomCode ?? null);

  /** A dealer is needed as soon as any row is work on his own property. */
  const needsDealer = useMemo(
    () => lines.some((l) => subTypeOf(l)?.dealerReferenceRequired === true),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lines, cat],
  );

  const setLine = (key: string, patch: Partial<DraftLine>) => {
    setStatus(null);
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };

  const incomplete = (l: DraftLine): string | null => {
    if (!l.brandingTypeId) return 'a branding type';
    if (!l.brandingItemId) return 'a sub-type';
    if (namesAWorkOrder) {
      const c = coverOf(l);
      if (c === 'loading') return 'a moment while its work orders load';
      if (c === 'failed') return 'its work orders, which could not be loaded';
      if (Array.isArray(c) && c.length === 0)
        return 'a work order — none is issued for this sub-type, so issue one first';
      if (Array.isArray(c) && coverLines(l).length === 0)
        return `a work order of ${jobVendorFor(l)?.vendorName ?? ''} — one order goes to one vendor, so place a separate order for another vendor`;
      if (Array.isArray(c) && c.length > 0 && !chosenWorkOrder(l))
        return 'the work order line it draws on';
    }
    if (!(Number(l.quantity) > 0)) return 'a quantity';
    const basis = basisOf(l);
    if (basis === 'AREA' && !(Number(l.height) > 0 && Number(l.width) > 0))
      return 'a height and a width';
    if (basis === 'SLOT' && !(Number(l.slotLength) > 0)) return 'a slot length';
    return null;
  };

  const blocker = (() => {
    if (!deptId) return 'Select the department raising this request';
    if (!memberId) return 'Select who in that department is raising it';
    const bad = lines.findIndex((l) => incomplete(l) !== null);
    if (bad >= 0) return `Row ${bad + 1} needs ${incomplete(lines[bad])}`;
    if (needsDealer && !customerId)
      return 'This work is done at a dealer’s premises, so name the dealer';
    return null;
  })();

  const save = async () => {
    if (blocker) return;
    setBusy(true);
    setError(null);
    try {
      const r = await createBrandingRequest(
        {
          customerId: customerId || undefined,
          planningCycleId: cycleId || undefined,
          remarks: remarks || undefined,
          requestingDeptId: deptId || undefined,
          requestingMemberId: memberId || undefined,
          lines: lines.map((l) => {
            const sub = subTypeOf(l);
            const basis = basisOf(l);
            return {
              brandingTypeId: l.brandingTypeId,
              brandingItemId: l.brandingItemId,
              uomId: sub?.uomId ?? undefined,
              uomCode: sub?.uomCode ?? '',
              quantity: Number(l.quantity),
              dim1: basis === 'AREA' ? Number(l.height) : undefined,
              dim2: basis === 'AREA' ? Number(l.width) : undefined,
              dim3: basis === 'SLOT' ? Number(l.slotLength) : undefined,
              dim3UomCode: basis === 'SLOT' ? l.slotUnit : undefined,
              siteLocation:
                basis === 'SLOT' ? undefined : l.siteLocation || undefined,
              serviceFromDate: basis === 'SLOT' ? l.fromDate || undefined : undefined,
              serviceToDate: basis === 'SLOT' ? l.toDate || undefined : undefined,
              workOrderId: chosenWorkOrder(l)?.workOrderId,
              workOrderLineId: l.workOrderLineId || undefined,
              remarks: l.remarks || undefined,
            };
          }),
        },
        token,
      );
      setSaved(r);
      setPicked(new Set());
      setStatus(`Request ${r.requestNo} saved`);
      setLines([newLine()]);
      setRemarks('');
      setCustomerId('');
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const forwardPicked = async () => {    if (!saved || picked.size === 0) return;
    setBusy(true);
    setError(null);
    try {
      const r = await forwardRequestLines([...picked], token);
      setSaved(r);
      setPicked(new Set());
      setStatus(
        r.skippedLineNumbers.length > 0
          ? `Forwarded ${r.forwarded}; rows ${r.skippedLineNumbers.join(', ')} were already sent`
          : `Forwarded ${r.forwarded} row${r.forwarded === 1 ? '' : 's'} to the Branding Officer`,
      );
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const toggle = (id: string) =>
    setPicked((p) => {
      const next = new Set(p);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  // Only rows the server calls forwardable can be forwarded (a draft, or one
  // whose earlier forward never started an approval), so select-all must not
  // tick rows already with somebody.
  const draftIds = useMemo(
    () => (saved?.lines ?? [])
      .filter((l) => l.forwardable)
      .map((l) => l.requestLineId),
    [saved],
  );

  const refresh = async () => {
    if (!saved) return;
    setSaved(await getBrandingRequest(saved.requestId, token));
  };

  const loadOrders = async () => {
    try {
      const r = await listBrandingRequests(token);
      setOrders(r.data);
    } catch { setOrders([]); }
  };

  useEffect(() => { void loadOrders(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token, saved]);

  const openOrder = async (id: string) => {
    setError(null);
    try {
      setSaved(await getBrandingRequest(id, token));
      setPicked(new Set());
      setStatus(null);
      // The order renders below the whole form, so without this the press
      // looked like it did nothing at all.
      requestAnimationFrame(() =>
        savedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      );
    } catch (e) { setError((e as Error).message); }
  };

  return (
    <main aria-label="branding-request-new">
      <h1>Raise request for branding activity</h1>
      {error ? <p role="alert">{error}</p> : null}
      {status ? <p role="status">{status}</p> : null}

      {/* An order saved earlier could not be reached again, so its draft rows
          could never be forwarded once this page was left. */}
      <section aria-label="saved-orders">
        <h2>Orders already saved</h2>
        {orders.length === 0 ? (
          <p aria-label="orders-empty">Nothing saved yet.</p>
        ) : (
          <table aria-label="orders-table">
            <thead>
              <tr><th>Order</th><th>Dealer</th><th>Rows</th><th>Not yet sent</th><th /></tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.requestId} aria-label={`order-${o.requestNo}`}>
                  <td>{o.requestNo}</td>
                  <td>{o.companyName}</td>
                  <td>{o.lineCount}</td>
                  <td>{o.draftCount}</td>
                  <td>
                    <button
                      type="button"
                      aria-label={`open-${o.requestNo}`}
                      onClick={() => { void openOrder(o.requestId); }}
                    >
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section aria-label="raised-by">
        <h2>Request raised by</h2>
        <label htmlFor="br-dept">Department</label>
        <select
          id="br-dept"
          value={deptId}
          onChange={(e) => { setDeptId(e.target.value); setMemberId(''); }}
          data-testid="field-dept"
        >
          <option value="">
            {people && people.departments.length === 0
              ? 'No department has any member yet'
              : 'Select…'}
          </option>
          {(people?.departments ?? []).map((d) => (
            <option key={d.deptId} value={d.deptId}>{d.deptName}</option>
          ))}
        </select>

        <label htmlFor="br-member">Member</label>
        <select
          id="br-member"
          value={memberId}
          onChange={(e) => setMemberId(e.target.value)}
          disabled={!deptId}
          data-testid="field-member"
        >
          <option value="">
            {!deptId ? 'Select a department first' : 'Select…'}
          </option>
          {membersOf(deptId).map((m) => (
            <option key={m.userId} value={m.userId}>
              {m.displayName}{m.designation ? ` — ${m.designation}` : ''}
            </option>
          ))}
        </select>
        {people && people.departments.length === 0 ? (
          // Only reachable when the whole tenant has no employee records; a
          // department that appears in the list always has someone in it.
          <p role="status" data-testid="no-staffed-departments">
            Nobody is recorded against a department yet, so this request cannot
            name who raised it. Add employees under Organisation → Employees.
          </p>
        ) : null}
      </section>

      <label htmlFor="br-cycle">Financial year</label>
      <select id="br-cycle" value={cycleId} onChange={(e) => setCycleId(e.target.value)} data-testid="field-cycle">
        <option value="">—</option>
        {(cat?.cycles ?? []).map((c) => (
          <option key={c.planningCycleId} value={c.planningCycleId}>{c.cycleName}</option>
        ))}
      </select>

      <section aria-label="request-lines">
        <h2>Branding required</h2>
        {lines.map((l, idx) => (
          <fieldset key={l.key} data-testid={`line-${idx}`}>
            <legend>Row {idx + 1}</legend>

            <fieldset data-testid={`line-type-${idx}`}>
              <legend>Branding type</legend>
              {/* Radios, not a dropdown: there are exactly four and the choice
                  decides which sub-types appear, so it should be visible at a
                  glance rather than hidden behind a click. */}
              {(cat?.types ?? []).map((t) => (
                <label key={t.brandingTypeId}>
                  <input
                    type="radio"
                    name={`type-${l.key}`}
                    value={t.brandingTypeId}
                    checked={l.brandingTypeId === t.brandingTypeId}
                    onChange={() =>
                      setLine(l.key, {
                        brandingTypeId: t.brandingTypeId,
                        brandingItemId: '',
                      })
                    }
                    aria-label={`${t.typeName} row ${idx + 1}`}
                  />
                  {t.typeName}
                </label>
              ))}
            </fieldset>

            <fieldset data-testid={`line-item-${idx}`}>
              <legend>Sub-type</legend>
              {!l.brandingTypeId ? (
                <p>Select a branding type first</p>
              ) : itemsOf(l.brandingTypeId).length === 0 ? (
                <p>
                  This type has no sub-types yet. Add one under Master Config →
                  Branding Types &amp; Subtypes.
                </p>
              ) : (
                itemsOf(l.brandingTypeId).map((i) => (
                  <label key={i.brandingItemId}>
                    <input
                      type="radio"
                      name={`item-${l.key}`}
                      value={i.brandingItemId}
                      checked={l.brandingItemId === i.brandingItemId}
                      onChange={() => setLine(l.key, { brandingItemId: i.brandingItemId })}
                      aria-label={`${i.itemName} row ${idx + 1}`}
                    />
                    {i.itemName}
                  </label>
                ))
              )}
            </fieldset>

            {/* An order draws on a commitment already made to a vendor, so the
                work order is shown before anything is measured. A requester who
                cannot issue work orders never sees this. */}
            {l.brandingItemId && namesAWorkOrder ? (
              <fieldset data-testid={`line-workorder-${idx}`}>
                <legend>Work order</legend>
                {coverOf(l) === 'loading' ? (
                  <p>Looking for work orders covering this sub-type…</p>
                ) : coverOf(l) === 'failed' ? (
                  <p role="alert" data-testid={`line-workorder-failed-${idx}`}>
                    The work orders for this sub-type could not be loaded.
                  </p>
                ) : allCoverLines(l).length === 0 ? (
                  <p role="alert" data-testid={`line-no-workorder-${idx}`}>
                    No approved work order is issued for this sub-type. Issue a
                    work order for it first, then raise this order.
                  </p>
                ) : coverLines(l).length === 0 ? (
                  <p role="alert" data-testid={`line-other-vendor-${idx}`}>
                    {`This order goes to ${jobVendorFor(l)?.vendorName ?? ''}, and none of its work orders covers this sub-type. One order goes to one vendor — place a separate order for another vendor's work.`}
                  </p>
                ) : (
                  <table data-testid={`line-workorder-table-${idx}`}>
                    <thead>
                      <tr>
                        <th />
                        <th>Work order</th>
                        <th>Line</th>
                        <th>Vendor</th>
                        <th>Where</th>
                        <th>Committed</th>
                        <th>Already ordered</th>
                        <th>Rate</th>
                        <th>Valid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {coverLines(l).map((c) => (
                        <tr key={c.workOrderLineId}>
                          <td>
                            {/* One order draws on ONE work order line — the row
                                already holds a single workOrderLineId, and the
                                line is incomplete without it. A checkbox invited
                                a second tick and offered an un-tick that leaves
                                the line unusable; a radio says what the data
                                means. Grouped per draft line, so two lines on
                                the screen stay independent of each other. */}
                            <input
                              type="radio"
                              name={`work-order-line-${l.key}`}
                              checked={l.workOrderLineId === c.workOrderLineId}
                              onChange={() =>
                                setLine(l.key, {
                                  workOrderLineId: c.workOrderLineId,
                                })
                              }
                              aria-label={`${c.woNumber} line ${c.lineNumber} row ${idx + 1}`}
                            />
                          </td>
                          <td>{c.woNumber}</td>
                          <td>{c.lineNumber}</td>
                          <td>{`${c.vendorCode} — ${c.vendorName}`}</td>
                          <td>{c.geoNodeName ?? 'All India'}</td>
                          <td>{`${decimal(c.committedQuantity)} ${c.uomCode ?? ''}`}</td>
                          <td>{decimal(c.orderedQuantity)}</td>
                          <td>{c.rateValue == null ? '—' : decimal(c.rateValue)}</td>
                          <td>
                            {dayRange(c.validFromDate, c.validToDate)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </fieldset>
            ) : null}

            {/* Nothing below appears until a sub-type is chosen: the sub-type
                carries the unit, and the unit is what decides these fields. */}
            {l.brandingItemId ? (
              <fieldset data-testid={`line-measure-${idx}`}>
                <legend>
                  {`Measured in ${subTypeOf(l)?.uomName ?? subTypeOf(l)?.uomCode ?? '—'}`}
                </legend>

                <label htmlFor={`q-${l.key}`}>Quantity</label>
                <input
                  id={`q-${l.key}`}
                  value={l.quantity}
                  onChange={(e) => setLine(l.key, { quantity: e.target.value })}
                  data-testid={`line-qty-${idx}`}
                />

                {basisOf(l) === 'AREA' ? (
                  <span data-testid={`line-area-fields-${idx}`}>
                    <label htmlFor={`h-${l.key}`}>Height</label>
                    <input
                      id={`h-${l.key}`}
                      value={l.height}
                      onChange={(e) => setLine(l.key, { height: e.target.value })}
                      data-testid={`line-height-${idx}`}
                    />
                    <label htmlFor={`w-${l.key}`}>Width</label>
                    <input
                      id={`w-${l.key}`}
                      value={l.width}
                      onChange={(e) => setLine(l.key, { width: e.target.value })}
                      data-testid={`line-width-${idx}`}
                    />
                    <span data-testid={`line-measured-${idx}`}>
                      {`= ${number(
                        Number(l.quantity || 0) *
                        Number(l.height || 0) *
                        Number(l.width || 0),
                      )} ${subTypeOf(l)?.uomCode ?? ''}`}
                    </span>
                  </span>
                ) : null}

                {basisOf(l) === 'SLOT' ? (
                  <span data-testid={`line-slot-fields-${idx}`}>
                    <label htmlFor={`s-${l.key}`}>Slot length</label>
                    <input
                      id={`s-${l.key}`}
                      value={l.slotLength}
                      onChange={(e) => setLine(l.key, { slotLength: e.target.value })}
                      data-testid={`line-slot-${idx}`}
                    />
                    <select
                      aria-label={`Slot unit row ${idx + 1}`}
                      value={l.slotUnit}
                      onChange={(e) => setLine(l.key, { slotUnit: e.target.value })}
                      data-testid={`line-slot-uom-${idx}`}
                    >
                      <option value="SECOND">Second</option>
                      <option value="DURATION">Duration</option>
                    </select>
                    <label htmlFor={`df-${l.key}`}>Runs from</label>
                    <DateInput
                      id={`df-${l.key}`}
                      type="date"
                      value={l.fromDate}
                      onValueChange={(nextValue) => setLine(l.key, { fromDate: nextValue })}
                      data-testid={`line-from-${idx}`}
                    />
                    <label htmlFor={`dt-${l.key}`}>Runs to</label>
                    <DateInput
                      id={`dt-${l.key}`}
                      type="date"
                      value={l.toDate}
                      onValueChange={(nextValue) => setLine(l.key, { toDate: nextValue })}
                      data-testid={`line-to-${idx}`}
                    />
                  </span>
                ) : (
                  <span data-testid={`line-place-fields-${idx}`}>
                    <label htmlFor={`pl-${l.key}`}>
                      {basisOf(l) === 'AREA' ? 'Where it stands' : 'Where it goes (optional)'}
                    </label>
                    <input
                      id={`pl-${l.key}`}
                      value={l.siteLocation}
                      onChange={(e) => setLine(l.key, { siteLocation: e.target.value })}
                      data-testid={`line-place-${idx}`}
                    />
                  </span>
                )}
              </fieldset>
            ) : null}

            <label htmlFor={`r-${l.key}`}>Remarks</label>
            <input
              id={`r-${l.key}`}
              value={l.remarks}
              onChange={(e) => setLine(l.key, { remarks: e.target.value })}
              data-testid={`line-remarks-${idx}`}
            />

            {lines.length > 1 ? (
              <Button
                type="button"
                variant="subtle"
                onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}
                data-testid={`remove-line-${idx}`}
              >
                Remove
              </Button>
            ) : null}
          </fieldset>
        ))}
        <Button type="button" variant="secondary" onClick={() => setLines((ls) => [...ls, newLine()])} data-testid="add-line">
          Add row
        </Button>
      </section>

      {/* Only asked where the sub-type says the work is on the dealer's own
          property — painting his wall or his shop, or his signboard. A TV spot,
          a highway hoarding and POP material delivered to the office have no
          dealer at this stage, and the server files those against the tenant's
          placeholder dealer so reports still have a row to group by. */}
      {needsDealer ? (
        <section aria-label="dealer" data-testid="dealer-section">
          <h2>Dealer / Retailer</h2>
          <p data-testid="dealer-why">
            One of the rows is work done at a dealer’s premises, so this request
            must name him.
          </p>
          <label htmlFor="br-dealer">Dealer / Retailer</label>
          <select
            id="br-dealer"
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            data-testid="field-dealer"
          >
            <option value="">
              {dealers.length === 0 ? 'No active dealers yet' : 'Select…'}
            </option>
            {dealers.map((d) => (
              <option key={d.customerId} value={d.customerId}>
                {d.dealerCode} — {d.companyName} ({d.dealerType})
              </option>
            ))}
          </select>

          {dealer ? (
            <section aria-label="dealer-address" data-testid="dealer-address">
              <h3>Address</h3>
              <p>{dealer.address ?? '—'}</p>
              <p>
                {[dealer.city, dealer.districtCode, dealer.stateCode, dealer.pinCode]
                  .filter(Boolean).join(' · ')}
              </p>
              <p>
                {[dealer.contactPerson, dealer.mobileNumber].filter(Boolean).join(' · ') || '—'}
              </p>
            </section>
          ) : null}
        </section>
      ) : null}

      <label htmlFor="br-remarks">Remarks</label>
      <textarea id="br-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="field-remarks" />

      {/* Says what is still needed, not that something went wrong: an alert on
          an untouched form reads as a failure the user has not caused yet. */}
      {blocker ? <p role="status" data-testid="br-blocker">{blocker}</p> : null}
      <Button type="button" variant="primary" onClick={() => void save()} disabled={!!blocker || busy} data-testid="save-request">
        Save request
      </Button>

      {saved ? (
        <section aria-label="saved-request" ref={savedRef}>
          <h2>{saved.requestNo}</h2>
          <p>{saved.companyName} · {saved.city ?? ''} · {saved.districtCode}</p>

          <table aria-label="saved-lines">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    aria-label="select-all-rows"
                    data-testid="select-all"
                    checked={
                      draftIds.length > 0 && draftIds.every((id) => picked.has(id))
                    }
                    onChange={(e) =>
                      setPicked(new Set(e.target.checked ? draftIds : []))
                    }
                    disabled={draftIds.length === 0}
                  />
                </th>
                <th>#</th><th>Type</th><th>Sub-type</th><th>Qty</th>
                <th>Status</th><th>Forwarded</th><th>Pending with</th>
              </tr>
            </thead>
            <tbody>
              {saved.lines.map((l) => (
                <tr key={l.requestLineId} data-testid={`saved-line-${l.lineNumber}`}>
                  <td>
                    {l.forwardable ? (
                      <input
                        type="checkbox"
                        aria-label={`select-row-${l.lineNumber}`}
                        data-testid={`select-${l.lineNumber}`}
                        checked={picked.has(l.requestLineId)}
                        onChange={() => toggle(l.requestLineId)}
                      />
                    ) : null}
                  </td>
                  <td>{l.lineNumber}</td>
                  <td>{l.typeName}</td>
                  <td>{l.itemName ?? '—'}</td>
                  <td>{orderLineQuantityLabel(l, l.quantity, decimal)}</td>
                  <td>{l.lineStatusCode}</td>
                  <td>{l.forwardedAt ? at(l.forwardedAt) : '—'}</td>
                  <td>{l.pendingWith ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <p data-testid="picked-count">{picked.size} selected</p>
          <Button
            type="button"
            variant="primary"
            onClick={() => void forwardPicked()}
            disabled={busy || picked.size === 0}
            title={picked.size === 0 ? 'Tick the rows to forward' : undefined}
            data-testid="forward-selected"
          >
            Forward
          </Button>

          <h3>Status history</h3>
          <Button type="button" variant="subtle" onClick={() => void refresh()} data-testid="refresh-history">
            Refresh
          </Button>
          <table aria-label="status-history">
            <thead>
              <tr><th>When</th><th>From</th><th>To</th><th>By</th><th>Note</th></tr>
            </thead>
            <tbody>
              {saved.history.map((h, i) => (
                <tr key={`${at(h.changedAt)}-${i}`} data-testid={`history-${i}`}>
                  <td>{at(h.changedAt)}</td>
                  <td>{h.fromStatus ?? '—'}</td>
                  <td>{h.toStatus}</td>
                  <td>{h.changedByName ?? '—'}</td>
                  <td>{h.remarks ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}
    </main>
  );
}
